import React, { useState } from "react";
import { verifyAccessCode, isUnlocked, rememberUnlock } from "../config/access";
import Icon from "./Icon";

/**
 * Wraps gated content. Renders `children` once unlocked; otherwise shows a
 * lock form with an optional `teaser` describing what's behind it.
 *
 * Verification is async and delegated entirely to config/access.js, so
 * moving the check to a server later needs no change here — this component
 * already handles a pending state and a network failure message, which a
 * synchronous string comparison would never have produced.
 */
export default function AccessGate({ storageKey, title = "Enter access code", teaser, children }) {
  const [unlocked, setUnlocked] = useState(() => isUnlocked(storageKey));
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (checking) return;
    setChecking(true);
    setError("");

    const result = await verifyAccessCode(code);
    setChecking(false);

    if (result.ok) {
      setUnlocked(true);
      rememberUnlock(storageKey);
      return;
    }
    setError(result.message || "That code isn't right — try again.");
    setCode("");
  };

  if (unlocked) return children;

  return (
    <div className="gate-card">
      <div className="gate-lock" aria-hidden="true">
        <Icon name="aperture" size={26} />
      </div>
      <h2>{title}</h2>
      {teaser && <p className="gate-teaser">{teaser}</p>}
      <form onSubmit={handleSubmit}>
        <label className="gate-label" htmlFor={`code-${storageKey}`}>Access code</label>
        <input
          id={`code-${storageKey}`}
          type="text"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          placeholder="Enter code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={checking}
          aria-describedby={error ? `err-${storageKey}` : undefined}
          aria-invalid={error ? "true" : undefined}
        />
        <button type="submit" disabled={checking}>
          {checking ? "Checking…" : "Unlock"}
        </button>
      </form>
      {error && (
        <p className="gate-error" id={`err-${storageKey}`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
